import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAlertForm } from '../useAlertForm';
import { selectSubaccount } from 'src/selectors/subaccounts';
import { getSubaccount } from 'src/actions/subaccounts';
import { WeekDays } from '../constants';

import { list } from '@sparkpost/report-builder/config';
import { useAnalyticsReportContext } from 'src/pages/analyticsReport/context/AnalyticsReportContext';
import { hydrateFilters } from 'src/helpers/analyticsReport';

type UseFillAlertForm = {
  handleFillAlertForm: (alertId: number) => void;
};

export function useFillAlertForm(): UseFillAlertForm {
  const { dispatch } = useAlertForm();
  const reduxDispatch = useDispatch();
  const {
    actions: { setFilters }
  } = useAnalyticsReportContext() as $TODOFIXME;
  const [shouldDispatchSubaccountInfo, setShouldDispatchSubaccountInfo] = useState(false);

  // TODO-TS: remove and and type correctly
  const subaccount = useSelector<$TODOFIXME, $TODOFIXME>(selectSubaccount);

  const handleFillAlertForm = useCallback<UseFillAlertForm['handleFillAlertForm']>(
    (alertId) => {
      // TODO: Implement API
      const alertMock = {
        id: 117,
        customer: 101,
        tenant: 'spc',
        subaccount: 12244,
        name: 'Alert Name Here',
        alert_type: 1,
        description: 'Alert description here',
        status: 'active',
        created: '2017-07-23T00:00:00',
        updated: '2017-07-23T00:00:00',
        incident_status: 'closed',
        alert_configuration: {
          metrics: 'count_bounce',
          filters: {
            AND: {
              domains: {
                eq: ['gmail.com', 'yahoo.com', 'hotmail.com'],
                like: ['mail']
              },
              sending_domains: {
                notEq: ['sparkpost.com'],
                notLike: ['.io']
              }
            }
          },
          conditions: {
            operator: 'gt',
            value: 10,
            precision: 'hour'
          },
          notification_schedule: {
            days: [1, 2, 3, 4, 5],
            timeframe: {
              start: '00:00:00',
              end: '23:59:59',
              time_zone: 'UTC'
            }
          },
          group_by: 'subaccount'
        }
      };

      const selectedMetric = list.find(({ key }) => key === alertMock.alert_configuration.metrics);

      const accountData = {
        assignment: 'primary',
        subaccount: {}
      };

      const measurementData = {
        measurementType: 'by-breakdown'
      };

      if (alertMock.subaccount > 0) {
        // request subaccount info
        setShouldDispatchSubaccountInfo(true);
        reduxDispatch(getSubaccount(alertMock.subaccount));
      }

      if (alertMock.alert_configuration.filters) {
        setFilters(hydrateFilters([alertMock.alert_configuration.filters]));
      }

      dispatch({ type: 'UPDATE_METRIC_DATA', data: { selectedMetric } });
      dispatch({ type: 'UPDATE_MEASUREMENT_DATA', data: { measurementData } });
      dispatch({ type: 'UPDATE_ACCOUNT_DATA', data: accountData });
      dispatch({ type: 'UPDATE_ALERT_DATA', data: { name: `Copy of ${alertMock.name}` } });
      dispatch({
        type: 'UPDATE_CONDITION_DATA',
        data: {
          // TODO: check condition types
          condition: alertMock.alert_configuration.conditions.operator,
          alertThreshold: alertMock.alert_configuration.conditions.value,
          precision: alertMock.alert_configuration.conditions.precision
        }
      });

      // TODO: verify notification data (it's not in https://github.com/SparkPost/sparkpost-admin-api-documentation/blob/master/services/alertsv2_api.md#alert-object)
      dispatch({
        type: 'UPDATE_NOTIFICATION_DATA',
        data: {
          isEmailChecked: true, // isSlackChecked | isWebhookChecked
          emails: ['example@example.com'], // webhook: String, slack: String,
          time: {
            days: alertMock.alert_configuration.notification_schedule.days.map(
              (day) => WeekDays[day]
            ),
            endPeriod: 'AM',
            endTime: '',
            range: 'defined', // if we have a range of days
            startPeriod: 'AM',
            startTime: '',
            timeZone: ''
          }
        }
      });
    },
    [dispatch, reduxDispatch, setFilters]
  );

  // receive subaccount info and dispatch to the form
  useEffect(() => {
    if (shouldDispatchSubaccountInfo && subaccount.id) {
      dispatch({
        type: 'UPDATE_ACCOUNT_DATA',
        data: {
          assignment: 'subaccount',
          subaccount: {
            id: subaccount.id,
            type: 'subaccounts',
            value: `${subaccount.name} (ID ${subaccount.id})`
          }
        }
      });
    }
  }, [shouldDispatchSubaccountInfo, subaccount, dispatch]);

  return {
    handleFillAlertForm
  };
}
